import CampusSelect from "components/CampusSelect"
import {FormLabel} from "components/Form"
import {KlassDropdown} from "components/Select"
import {KlassappTableHOC} from "HOC"
import {useCallback, useEffect, useState} from "react"
import {useDebounce} from "react-use"
import {majorVersionsService} from "services"
import {KlassappTableProps} from "types/common"
import {BaseNewFilter, KlassappTable, KlassappTableHeader} from "uiKit"
import {Major} from "types/major"
import {useQuery} from "@tanstack/react-query"
import {Col, Row} from "antd"
import {Campus} from "types/campus"
import styles from "./ProgramTable.module.css"

const statusOptions = [
    {value: null, label: "All"},
    {value: true, label: "Active"},
    {value: false, label: "Inactive"}
]

const INITIAL_FILTER = {
    campuses: [] as Campus.Campus[],
    isActive: true
}

type ProgramTableProps = KlassappTableProps & {
    setSelectedProgram: (program: Major.MajorVersion) => void
}

const ProgramTable = (props: ProgramTableProps) => {
    const {
        dispatch,
        dispatchFunc,
        setSelectedProgram,
        page,
        pageSize,
        allFields,
        fields,
        tableHeaderActions,
        orderField,
        columns,
        isShowTableHeaderAction
    } = props
    const [search, setSearch] = useState("")
    const [searchDebounce, setSearchDebounce] = useState("")
    const [filter, setFilter] = useState(INITIAL_FILTER)
    const [appliedFilter, setAppliedFilter] = useState(INITIAL_FILTER)
    useDebounce(() => setSearchDebounce(search), 500, [search])

    const campusIds = appliedFilter.campuses?.length ? appliedFilter.campuses.map((campus) => campus.id) : undefined
    const isActive = appliedFilter.isActive

    const {data: programVersions, isFetching} = useQuery(
        ["list-program-versions", page, pageSize, searchDebounce, orderField, campusIds, isActive],
        async () => {
            const response = await majorVersionsService.getAll({
                range: {page, pageSize: pageSize},
                linkedEntities: true,
                filter: {
                    search: searchDebounce,
                    type: Major.Unit.Clock,
                    campusIds,
                    ...(isActive !== null ? {isActive} : {})
                },
                sort: {orderBy: orderField.field ?? "id", orderDir: orderField.order ?? "DESC"}
            })
            if (response.data) {
                response.data = response.data.map((item) => {
                    return {
                        ...item,
                        programName: item.program.name,
                        programCode: item.program.code
                    }
                })
            }
            return response
        },
        {
            initialData: {
                data: [],
                total: 0
            }
        }
    )

    const renderStatus = (status) => {
        return <p className={`status-${status} ${styles.status}`}>{status}</p>
    }

    const getFields = () => {
        return ["Program Name", "Program Code", "Program Version Code", "Program Version Name", "Campuses", "Status"]
    }

    const getPageTitle = () => {
        return "Major"
    }

    const getColumns = () => {
        return [
            {
                title: "Program Name",
                field: "programName"
            },
            {
                title: "Program Code",
                field: "programCode"
            },
            {
                title: "Program Version Code",
                field: "version_code",
                orderField: "versionCode",
                sortable: true
            },
            {
                title: "Program Version Name",
                field: "version_name",
                orderField: "versionName",
                sortable: true
            },
            {
                title: "Campuses",
                field: "campuses",
                render: (campuses) => campuses.map((campus) => campus.name).join(", ")
            },
            {
                title: "Status",
                field: "isActive",
                render: (isActive) => renderStatus(isActive ? "active" : "inactive")
            }
        ]
    }

    useEffect(() => {
        dispatch({isClassComponent: false})
        dispatchFunc([
            {key: "getPageTitle", func: getPageTitle},
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns}
        ])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [programVersions])

    const onClickRowItem = (row) => {
        setSelectedProgram(row)
    }

    const onChangeFilter = useCallback(<K extends keyof typeof filter>(key: K, value: typeof filter[K]) => {
        setFilter((prev) => ({...prev, [key]: value}))
    }, [])

    const onClearFilter = useCallback(() => {
        setFilter(INITIAL_FILTER)
        setAppliedFilter(INITIAL_FILTER)
    }, [])

    const onApplyFilter = useCallback(() => {
        setAppliedFilter(filter)
    }, [filter])

    const onSearchInput = useCallback((value = "") => {
        setSearch(value)
    }, [])

    return (
        <>
            <BaseNewFilter
                filter={filter}
                searchValue={search}
                onSearchInput={onSearchInput}
                onClick={onApplyFilter}
                onClickClear={onClearFilter}>
                <Row gutter={[40, 32]}>
                    <Col span={12}>
                        <FormLabel label="Campus" />
                        <CampusSelect
                            isMulti
                            isClearable
                            placeholder="Campus"
                            value={filter.campuses}
                            onChange={(newValue) => onChangeFilter("campuses", newValue ?? [])}
                        />
                    </Col>
                    <Col span={12}>
                        <FormLabel label="Status" />
                        <KlassDropdown
                            valueKey="value"
                            labelKey="label"
                            placeholder="Status"
                            isClearable
                            value={statusOptions.find((opt) => opt.value === filter.isActive)}
                            options={statusOptions}
                            onChange={(newValue) => onChangeFilter("isActive", newValue?.value ?? null)}
                        />
                    </Col>
                </Row>
            </BaseNewFilter>
            <KlassappTableHeader
                isShowAction={isShowTableHeaderAction}
                actions={tableHeaderActions}
                page={page}
                total={programVersions.total}
                defaultPageSize={pageSize}
                onChangePage={props.onChangePage}
                onChangeRowPerPage={props.onChangeRowPerPage}
            />
            <KlassappTable
                columns={columns}
                fields={fields}
                data={programVersions.data}
                menuActions={[]}
                isLoading={isFetching}
                allFields={allFields}
                isShowCheckedColumn={false}
                orderField={orderField}
                onClickRowItem={onClickRowItem}
                onChangeFields={props.onChangeFields}
                onChangeAllFields={props.onChangeAllFields}
                onUpdateRowData={props.onUpdateRowData}
                onClickSortColumn={props.onClickSortColumn}
                onUpdateTableData={props.onUpdateTableData}
                onDraggableColumn={props.onDraggableColumn}
            />
        </>
    )
}

export default KlassappTableHOC(ProgramTable)
